import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentPasteGo from "@mui/icons-material/ContentPasteGo";
import Button from "@components/Button/Button";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext.tsx";
import LicenseCard from "@features/licensing/license-details/components/LicenseCard.tsx";
import { useUploadOtherLicenseDocuments } from "@features/licensing/license-details/flow/request-port-operator-license/hooks/useUploadOtherLicenseDocuments.tsx";
import { useDownloadLicenseDocument } from "@features/licensing/license-details/hooks/useDownloadLicenseDocument.tsx";
import { getFileExtensionFromUrl } from "@features/licensing/license-details/utils.tsx";
import { isGovernmentCompany } from "@features/licensing/schema/licenseRequestValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  buildFormDraftFromBaseline,
  clearFormDraft,
  mergeFormDraft,
  readFormDraft,
  writeFormDraft,
} from "@hooks/formDraftPersistence";
import { useCompanyTypes } from "@hooks/useCompanyTypes";
import { tradeLicenseIssuingAuthorities } from "@pages/vendor-license-request/constants.ts";
import { invalidateLicenseListQueries } from "@pages/vendor-license-request/hooks/useCreateVendorLicenseRequest";
import { useExternalDocumentUpload } from "@pages/vendor-license-request/hooks/useExternalDocumentUpload";
import { useSyncCompanyType } from "@pages/vendor-license-request/hooks/useSyncCompanyType.ts";
import { getCompanyTypeOptions } from "@pages/vendor-license-request/utils.ts";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { DocumentTypes } from "@services/licensing/types";
import {
  createPortOperatorLicenseRenewalRequest,
  createPortOperatorLicenseRequest,
  getCurrentVendorLicense,
  getPortOperatorLicenseById,
} from "@services/licensingApi";
import { SasTokenPrefix } from "@services/user/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { completeScreenNavigation } from "@telemetry/trackNavigation";
import { trackScreenLoad } from "@telemetry/trackScreenLoad";
import { paths } from "@utils/navigation.tsx";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic.ts";
import { FormProvider, useForm, type FieldErrors, type FieldPath } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { PORT_OPERATOR_LICENSE_REQUEST_FIELDS as F } from "./constants.ts";
import {
  getGovernmentPortLicenseRequestFormSchema,
  getPortLicenseRequestFormSchema,
  mapPortLicenseRequestToPayload,
  portLicenseRequestDefaultValues,
  type PortLicenseRequestFormData,
} from "./schema/portOperatorLicenseRequestSchema.ts";
import { isApprovedVendorLicense, mapLicenseToPortForm } from "./utils.tsx";

const TEST_ID_BASE = "port-license-request";
const LANG_BASE = "licensing";
const EDITABLE_FIELDS_STORAGE_KEY = "port-operator-license-request-editable-fields";
const EDITABLE_FIELDS_STORAGE_VERSION = 2;
const DRAFT_STORAGE_KEY = "port-operator-license-request-draft";
const DRAFT_IGNORED_FIELDS: string[] = [F.TRADE_LICENSE_DOCUMENT_URL, F.OTHERS_DOCUMENT_URL];
const DRAFT_PERSISTED_FIELDS = (Object.values(F) as string[]).filter(
  (field) => !DRAFT_IGNORED_FIELDS.includes(field),
);

type EditableFieldsSnapshot = {
  fields: PortFieldName[];
  sourceKey: string | null;
  version: number;
};

type PortFieldName = FieldPath<PortLicenseRequestFormData>;

const emptyArray: never[] = [];
const othersFieldName = F.OTHERS_DOCUMENT_URL;
const ALWAYS_READONLY_FIELDS = new Set<PortFieldName>([F.COMPANY_NAME, F.COMPANY_NAME_ARABIC]);
const NON_EDITABLE_FORM_FIELDS = new Set<PortFieldName>([
  F.TRADE_LICENSE_DOCUMENT_URL,
  F.OTHERS_DOCUMENT_URL,
  F.FUJAIRAH_REGISTERED_COMPANY,
]);
const AUTO_VALIDATABLE_FIELDS: PortFieldName[] = (Object.values(F) as PortFieldName[]).filter(
  (field) => !ALWAYS_READONLY_FIELDS.has(field) && !NON_EDITABLE_FORM_FIELDS.has(field),
);
const isStoredEditableField = (field: string): field is PortFieldName => {
  const typedField = field as PortFieldName;
  return !ALWAYS_READONLY_FIELDS.has(typedField) && !NON_EDITABLE_FORM_FIELDS.has(typedField);
};

const buildUploadEntrySignature = (entry: UploadEntry) => {
  if (entry instanceof File) {
    return ["file", entry.name, entry.size, entry.type, entry.lastModified].join("|");
  }

  return [
    "upload",
    entry.name ?? "",
    entry.url ?? "",
    entry.type ?? "",
    entry.certificateType ?? "",
  ].join("|");
};

const areUploadEntriesEqual = (left: UploadEntry[], right: UploadEntry[]) => {
  if (left.length !== right.length) return false;

  return left.every(
    (entry, index) => buildUploadEntrySignature(entry) === buildUploadEntrySignature(right[index]),
  );
};

const readEditableFieldsFromStorage = (storageKey: string): EditableFieldsSnapshot => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { fields: [], sourceKey: null, version: EDITABLE_FIELDS_STORAGE_VERSION };

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") {
      return { fields: [], sourceKey: null, version: EDITABLE_FIELDS_STORAGE_VERSION };
    }

    if (parsed.version !== EDITABLE_FIELDS_STORAGE_VERSION || !Array.isArray(parsed.fields)) {
      return { fields: [], sourceKey: null, version: EDITABLE_FIELDS_STORAGE_VERSION };
    }

    return {
      fields: parsed.fields.filter(
        (field: unknown): field is PortFieldName =>
          typeof field === "string" && isStoredEditableField(field),
      ),
      sourceKey: typeof parsed.sourceKey === "string" ? parsed.sourceKey : null,
      version: EDITABLE_FIELDS_STORAGE_VERSION,
    };
  } catch {
    return { fields: [], sourceKey: null, version: EDITABLE_FIELDS_STORAGE_VERSION };
  }
};

const PortLicenseRequestPage = () => {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const { renewalId } = useParams();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const userCompanyId = useAuthStore((state) => state.user?.companyId ?? null);
  const isRenewalMode = Boolean(renewalId);
  const tradeLicenseUploadStoreKey = isRenewalMode
    ? `port-operator-renewal-${renewalId}-${F.TRADE_LICENSE_DOCUMENT_URL}`
    : undefined;
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const documentConditions = useMemo(
    () => ({ ...FILE_UPLOAD_PROFILES.certificates, hideErrors: false }),
    [FILE_UPLOAD_PROFILES.certificates],
  );
  const draftStorageKey = isRenewalMode ? `${DRAFT_STORAGE_KEY}-${renewalId}` : DRAFT_STORAGE_KEY;
  const editableFieldsStorageKey = isRenewalMode
    ? `${EDITABLE_FIELDS_STORAGE_KEY}-${renewalId}`
    : EDITABLE_FIELDS_STORAGE_KEY;
  const draftValues = useMemo(
    () => readFormDraft<PortLicenseRequestFormData>(draftStorageKey),
    [draftStorageKey],
  );
  const hasInitialDraft = !!draftValues && Object.keys(draftValues).length > 0;
  const {
    data,
    isLoading: isVendorLicenseLoading,
    isError: isVendorLicenseError,
    error: vendorLicenseError,
  } = useQuery({
    queryKey: isRenewalMode
      ? ["portOperatorLicense", "renewal", renewalId]
      : ["vendorLicense", "current"],
    queryFn: () =>
      isRenewalMode ? getPortOperatorLicenseById(renewalId as string) : getCurrentVendorLicense(),
  });
  const {
    data: currentVendorLicenseData,
    isLoading: isCurrentVendorLicenseLoading,
    isError: isCurrentVendorLicenseError,
    error: currentVendorLicenseError,
  } = useQuery({
    queryKey: ["vendorLicense", "current", "portOperatorRenewalTradeLicense", renewalId],
    queryFn: () => getCurrentVendorLicense(),
    enabled: isRenewalMode,
  });

  const { user } = useAuthStore();
  const isCurrentVendorLicenseNotFound =
    (currentVendorLicenseError as { response?: { status?: number } } | null)?.response?.status ===
    404;
  const isBaseLicenseLoading =
    isVendorLicenseLoading || (isRenewalMode && isCurrentVendorLicenseLoading);
  const hasBaseLicenseError =
    isVendorLicenseError ||
    (isRenewalMode && isCurrentVendorLicenseError && !isCurrentVendorLicenseNotFound);
  const baseLicenseError =
    vendorLicenseError ?? (isCurrentVendorLicenseNotFound ? null : currentVendorLicenseError);

  const canAccessRenewal = !isRenewalMode
    ? true
    : Boolean(userCompanyId && data?.companyId && userCompanyId === data.companyId);
  const {
    data: companyTypes = [],
    isLoading: isCompanyTypesLoading,
    isError: isCompanyTypesError,
  } = useCompanyTypes();

  const schema = useMemo(() => getPortLicenseRequestFormSchema(t, companyTypes), [t, companyTypes]);
  const governmentSchema = useMemo(
    () => getGovernmentPortLicenseRequestFormSchema(t, companyTypes),
    [t, companyTypes],
  );
  const form = useForm<PortLicenseRequestFormData>({
    defaultValues: portLicenseRequestDefaultValues,
    mode: "onBlur",
    resolver: (values, context, options) =>
      zodResolver(
        isGovernmentCompany(companyTypes, values.companyTypes) ? governmentSchema : schema,
      )(values, context, { ...options }),
  });
  const { watch } = form;
  const isTradeLicenseRequired = !isGovernmentCompany(companyTypes, watch(F.COMPANY_TYPES));

  const { isVendorDisabled } = useSyncCompanyType(
    companyTypes,
    form,
    isCompanyTypesLoading,
    isCompanyTypesError,
  );
  const companyTypeOptions = getCompanyTypeOptions(companyTypes, isVendorDisabled);

  const tradeLicenseIssuingAuthorityOptions = useMemo(
    () =>
      tradeLicenseIssuingAuthorities.map((authority) => ({
        value: authority.value,
        label: authority.label,
      })),
    [],
  );

  const allowedExtensions = useMemo(
    () =>
      (documentConditions.format ?? []).map((ext) =>
        ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
      ),
    [documentConditions.format],
  );

  const initialEditableSnapshot = useMemo(
    () => readEditableFieldsFromStorage(editableFieldsStorageKey),
    [editableFieldsStorageKey],
  );
  const initialEditableFields = initialEditableSnapshot.fields;
  const [editableFields, setEditableFields] = useState<Set<PortFieldName>>(
    () => new Set(initialEditableFields),
  );
  const [hasCapturedInitialValidation, setHasCapturedInitialValidation] = useState(
    () => initialEditableFields.length > 0,
  );
  const editableFieldsSourceKeyRef = useRef<string | null>(initialEditableSnapshot.sourceKey);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const suspendDraftPersistenceRef = useRef(false);
  const suspendValidationCaptureRef = useRef(false);

  const persistEditableFields = useCallback(
    (fieldNames: PortFieldName[], sourceKey: string | null) => {
      editableFieldsSourceKeyRef.current = sourceKey;
      localStorage.setItem(
        editableFieldsStorageKey,
        JSON.stringify({
          version: EDITABLE_FIELDS_STORAGE_VERSION,
          fields: fieldNames,
          sourceKey,
        }),
      );
    },
    [editableFieldsStorageKey],
  );

  const clearEditableFields = useCallback(() => {
    setEditableFields(new Set());
    setHasCapturedInitialValidation(false);
    editableFieldsSourceKeyRef.current = null;
    localStorage.removeItem(editableFieldsStorageKey);
  }, [editableFieldsStorageKey]);

  const isFieldDisabled = useCallback(
    (fieldName: PortFieldName) => {
      if (isRenewalMode) return false;

      return ALWAYS_READONLY_FIELDS.has(fieldName) || !editableFields.has(fieldName);
    },
    [editableFields, isRenewalMode],
  );

  const tradeLicenseUpload = useExternalDocumentUpload(
    F.TRADE_LICENSE_DOCUMENT_URL,
    SasTokenPrefix.LICENSE_SERVICE,
    form,
    "TRADE_LICENSE",
    isRenewalMode ? "PortOperatorLicenseRenewal" : "PortOperatorLicenseRequest",
    tradeLicenseUploadStoreKey,
  );
  const tradeLicenseStoreKey = tradeLicenseUploadStoreKey ?? F.TRADE_LICENSE_DOCUMENT_URL;
  const {
    uploadEntries: tradeLicenseUploadEntries,
    setUploadEntries: setTradeLicenseUploadEntries,
  } = tradeLicenseUpload;
  const tradeLicenseUploadEntriesRef = useRef(tradeLicenseUploadEntries);
  const hasUserUploadedTradeLicenseRef = useRef(false);
  tradeLicenseUploadEntriesRef.current = tradeLicenseUploadEntries;

  const { handleDownload, handlePreview } = useDownloadLicenseDocument();

  const otherFiles = useUploadStore((s) => s.filesByKey[othersFieldName] ?? emptyArray);
  const persistedTradeLicenseEntries = useUploadStore(
    (s) => s.filesByKey[tradeLicenseStoreKey] ?? emptyArray,
  );
  const persistedTradeLicenseEntriesRef = useRef(persistedTradeLicenseEntries);
  persistedTradeLicenseEntriesRef.current = persistedTradeLicenseEntries;
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const updateFilesForKey = useUploadStore((s) => s.updateFilesForKey);

  const startedAt = useRef(performance.now());
  const hasTrackedInitialLoad = useRef(false);

  useEffect(() => {
    if (
      isBaseLicenseLoading ||
      hasBaseLicenseError ||
      baseLicenseError ||
      !data ||
      hasTrackedInitialLoad.current
    ) {
      return;
    }

    hasTrackedInitialLoad.current = true;
    const screenName = isRenewalMode
      ? "Port Operator License Renewal"
      : "Port Operator License Request";
    completeScreenNavigation(screenName);
    trackScreenLoad("Licenses", screenName, startedAt.current);
  }, [baseLicenseError, data, hasBaseLicenseError, isBaseLicenseLoading, isRenewalMode]);

  const baseValuesFromBackend = useMemo(() => {
    if (!data) return null;

    const baseValues =
      isRenewalMode && currentVendorLicenseData && !isCurrentVendorLicenseError
        ? mapLicenseToPortForm(currentVendorLicenseData, user)
        : mapLicenseToPortForm(data, user);

    if (isRenewalMode && !isApprovedVendorLicense(currentVendorLicenseData)) {
      return {
        ...baseValues,
        tradeLicenseDocumentUrl: [],
      };
    }

    return baseValues;
  }, [currentVendorLicenseData, data, isCurrentVendorLicenseError, isRenewalMode, user]);
  const validationSourceKey = useMemo(() => {
    if (!baseValuesFromBackend) return null;

    const source = AUTO_VALIDATABLE_FIELDS.reduce<Record<string, unknown>>((acc, field) => {
      acc[field] = baseValuesFromBackend[field as keyof PortLicenseRequestFormData];
      return acc;
    }, {});

    return JSON.stringify(source);
  }, [baseValuesFromBackend]);

  const getFailedEditableFieldsFromBaseline = useCallback(
    (values: PortLicenseRequestFormData): PortFieldName[] => {
      const selectedSchema = isGovernmentCompany(companyTypes, values.companyTypes)
        ? governmentSchema
        : schema;
      const parsed = selectedSchema.safeParse(values);
      if (parsed.success) return [];

      const failed = new Set<PortFieldName>();
      parsed.error.issues.forEach((issue) => {
        const topLevel = issue.path[0];
        if (typeof topLevel === "string" && isStoredEditableField(topLevel)) {
          failed.add(topLevel as PortFieldName);
        }
      });

      return Array.from(failed);
    },
    [companyTypes, governmentSchema, schema],
  );

  const handleInvalidSubmit = useCallback(
    (errors: FieldErrors<PortLicenseRequestFormData>) => {
      if (suspendValidationCaptureRef.current) return;
      if (hasCapturedInitialValidation) return;

      const failedFields = (Object.keys(errors) as PortFieldName[]).filter(
        (field) => !ALWAYS_READONLY_FIELDS.has(field) && !NON_EDITABLE_FORM_FIELDS.has(field),
      );

      if (!failedFields.length) {
        setHasCapturedInitialValidation(true);
        return;
      }

      const uniqueFields = Array.from(new Set(failedFields));
      setEditableFields(new Set(uniqueFields));
      persistEditableFields(uniqueFields, validationSourceKey);
      setHasCapturedInitialValidation(true);
    },
    [hasCapturedInitialValidation, persistEditableFields, validationSourceKey],
  );

  useEffect(() => {
    if (!baseValuesFromBackend) return;

    const mergedValues = mergeFormDraft(baseValuesFromBackend, draftValues);
    const baselineTradeLicenseEntries = (mergedValues.tradeLicenseDocumentUrl ??
      []) as UploadEntry[];
    const storeOtherDocuments = buildOtherDocumentsFromStore();
    const nextOtherDocuments = storeOtherDocuments.length
      ? storeOtherDocuments
      : (mergedValues.othersDocumentUrl ?? []);
    const currentTradeLicenseEntries = tradeLicenseUploadEntriesRef.current;
    const canRestorePersistedTradeLicense = isRenewalMode;
    const nextTradeLicenseEntries = hasUserUploadedTradeLicenseRef.current
      ? currentTradeLicenseEntries
      : canRestorePersistedTradeLicense &&
          !baselineTradeLicenseEntries.length &&
          persistedTradeLicenseEntriesRef.current.length &&
          areUploadEntriesEqual(currentTradeLicenseEntries, persistedTradeLicenseEntriesRef.current)
        ? currentTradeLicenseEntries
        : baselineTradeLicenseEntries;

    form.reset({
      ...mergedValues,
      tradeLicenseDocumentUrl: nextTradeLicenseEntries,
    });

    if (!areUploadEntriesEqual(currentTradeLicenseEntries, nextTradeLicenseEntries)) {
      setTradeLicenseUploadEntries(nextTradeLicenseEntries);
    }

    if (!storeOtherDocuments.length && nextOtherDocuments.length) {
      setFilesForKey(othersFieldName, nextOtherDocuments);
    }

    // restore files from UploadStore or backend-prefilled other documents
    form.setValue(othersFieldName, nextOtherDocuments);
  }, [baseValuesFromBackend, draftValues, form, setFilesForKey, setTradeLicenseUploadEntries]);

  useEffect(() => {
    if (!isRenewalMode || isBaseLicenseLoading || !data || !renewalId) return;
    if (canAccessRenewal) return;

    navigate(
      `/${paths.licenses.index}/${paths.licenses.licenseDetailsById.replace(":id", renewalId)}?licenseType=PORT_OPERATOR_LICENSE`,
      { replace: true },
    );
  }, [canAccessRenewal, data, isBaseLicenseLoading, isRenewalMode, navigate, renewalId]);

  useEffect(() => {
    if (!validationSourceKey) return;
    if (!editableFields.size) return;

    // If backend snapshot changed, ignore stale unlock list and recompute first-failure fields.
    if (editableFieldsSourceKeyRef.current !== validationSourceKey) {
      clearEditableFields();
    }
  }, [clearEditableFields, editableFields.size, hasCapturedInitialValidation, validationSourceKey]);

  useEffect(() => {
    const subscription = watch((values) => {
      if (suspendDraftPersistenceRef.current) return;
      if (!baseValuesFromBackend) return;

      const draft = buildFormDraftFromBaseline(
        values,
        baseValuesFromBackend,
        DRAFT_PERSISTED_FIELDS,
        DRAFT_IGNORED_FIELDS,
      );

      if (Object.keys(draft).length > 0) {
        writeFormDraft(draftStorageKey, draft);
        return;
      }

      if (hasInitialDraft) {
        writeFormDraft(draftStorageKey, draftValues!);
        return;
      }

      clearFormDraft(draftStorageKey);
    });

    return () => subscription.unsubscribe();
  }, [baseValuesFromBackend, draftStorageKey, draftValues, hasInitialDraft, watch]);

  useEffect(() => {
    if (suspendValidationCaptureRef.current) return;
    if (!data) return;

    const runInitialOrPersistedValidation = async () => {
      if (isRenewalMode) {
        setHasCapturedInitialValidation(true);
        return;
      }

      // If we already captured initial failures, keep showing errors for those fields.
      if (hasCapturedInitialValidation && editableFields.size > 0) {
        await form.trigger(Array.from(editableFields));
        return;
      }

      // Run first validation pass automatically on load/refresh.
      if (!hasCapturedInitialValidation) {
        const firstInvalidFields = getFailedEditableFieldsFromBaseline(baseValuesFromBackend!);

        if (firstInvalidFields.length) {
          // Mark as captured before async trigger to avoid effect re-entry loops.
          setHasCapturedInitialValidation(true);
          const uniqueFields = Array.from(new Set(firstInvalidFields));
          setEditableFields(new Set(uniqueFields));
          persistEditableFields(uniqueFields, validationSourceKey);

          // Show current validation state for unlocked fields on refresh.
          await form.trigger(uniqueFields);
          return;
        }

        setHasCapturedInitialValidation(true);
      }
    };

    void runInitialOrPersistedValidation();
  }, [
    data,
    editableFields,
    form,
    hasCapturedInitialValidation,
    isRenewalMode,
    persistEditableFields,
    validationSourceKey,
    baseValuesFromBackend,
    getFailedEditableFieldsFromBaseline,
  ]);

  const readonlyTradeLicense = isRenewalMode
    ? currentVendorLicenseData &&
      !isCurrentVendorLicenseError &&
      isApprovedVendorLicense(currentVendorLicenseData)
      ? mapLicenseToPortForm(currentVendorLicenseData, user).tradeLicenseDocumentUrl?.[0]
      : undefined
    : baseValuesFromBackend?.tradeLicenseDocumentUrl?.[0];
  const tradeUrl = readonlyTradeLicense?.url;

  const handleRemoveFile = (idx: number) => {
    const current = [...(form.getValues(othersFieldName) ?? [])];
    const next = current.filter((_v, i) => i !== idx);

    form.setValue(othersFieldName, next, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
  };

  const buildOtherDocumentsFromStore = (
    documentUrls: string[] = [],
  ): { name: string; url: string; type: string }[] => {
    const storeEntries = useUploadStore.getState().getFilesForKey(othersFieldName);
    let currentDocumentUrl = 0;

    return storeEntries.map((entry) => {
      if (entry instanceof File) {
        return {
          name: entry.name,
          url: documentUrls[currentDocumentUrl++],
          type: DocumentTypes.OTHER_DOCUMENT,
        };
      }

      return {
        name: entry.name ?? "",
        url: entry.url ?? "",
        type: DocumentTypes.OTHER_DOCUMENT,
      };
    });
  };

  const uploadOthers = useUploadOtherLicenseDocuments({
    telemetryScreen: isRenewalMode ? "PortOperatorLicenseRenewal" : "PortOperatorLicenseRequest",
    handleUploadSuccess: ({ documentUrls }) => {
      updateFilesForKey(othersFieldName, () => {
        const next = buildOtherDocumentsFromStore(documentUrls);
        form.setValue(othersFieldName, next, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
        return next;
      });
    },
    handleUploadError: (error, meta) => {
      console.error("Upload OTHER_DOCUMENT failed:", error);

      const toRemoveDescending = Array.from(
        { length: meta.count },
        (_, i) => meta.startIdx + i,
      ).reverse();

      toRemoveDescending.forEach((idx) => {
        const current = [...(form.getValues(othersFieldName) ?? [])];
        const next = current.filter((_v, i) => i !== idx);
        form.setValue(othersFieldName, next, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      });
    },
  });

  const handleUploadOthers = async (_: string, files: File[]) => {
    if (!files?.length) return;

    uploadOthers.mutate({
      files,
      startIdx: form.getValues(othersFieldName)?.length ?? 0,
    });
  };

  const handleTradeLicenseUpload = useCallback(
    (files: File[]) => {
      if (files.length) hasUserUploadedTradeLicenseRef.current = true;
      tradeLicenseUpload.handleUpload(files);
    },
    [tradeLicenseUpload.handleUpload],
  );

  const handleTradeLicenseRemove = useCallback(() => {
    hasUserUploadedTradeLicenseRef.current = false;
    tradeLicenseUpload.handleRemove();
  }, [tradeLicenseUpload.handleRemove]);

  const createRequest = useMutation({
    mutationFn: async (payload: ReturnType<typeof mapPortLicenseRequestToPayload>) => {
      return runTransaction(
        {
          module: "licensing",
          screen: isRenewalMode ? "PortOperatorLicenseRenewal" : "PortOperatorLicenseRequest",
          action: isRenewalMode
            ? "submit_port_operator_license_renewal_request"
            : "submit_port_operator_license_request",
          entityType: "license_request",
          ...(isRenewalMode && renewalId ? { entityId: renewalId } : {}),
        },
        (transactionId) =>
          isRenewalMode && renewalId
            ? createPortOperatorLicenseRenewalRequest({
                licenseId: renewalId,
                payload,
                ...(transactionId ? { transactionId } : {}),
              })
            : createPortOperatorLicenseRequest(payload, ...(transactionId ? [transactionId] : [])),
      );
    },
    onSuccess: async () => {
      suspendDraftPersistenceRef.current = true;
      suspendValidationCaptureRef.current = true;
      clearEditableFields();
      clearFormDraft(draftStorageKey);
      await invalidateLicenseListQueries(queryClient);
      notify({
        title: isRenewalMode
          ? t(`${LANG_BASE}.port-operator-license-renewal-title-notify-success`)
          : t(`${LANG_BASE}.port-operator-license-title-notify-success`),
        description: isRenewalMode
          ? t(`${LANG_BASE}.port-operator-license-renewal-description-notify-success`)
          : t(`${LANG_BASE}.port-operator-license-description-notify-success`),
        type: "success",
      });
      navigate(`/${paths.licenses.index}`);
      form.reset();
      handleTradeLicenseRemove();
      setFilesForKey(F.TRADE_LICENSE_DOCUMENT_URL, []);
      setFilesForKey(F.PORT_OPERATOR_LICENSE_DOCUMENT_URL, []);
      setFilesForKey(F.OTHERS_DOCUMENT_URL, []);
    },
    onError: async (error: any) => {
      notify({
        title: error?.response?.data?.detail ?? error?.message ?? t("common.something-went-wrong"),
        type: "error",
      });
    },
  });

  const onSubmit = (values: PortLicenseRequestFormData) => {
    const payload = mapPortLicenseRequestToPayload(values, companyTypes);
    createRequest.mutate(payload);
  };

  const handleCancel = () => {
    if (uploadOthers.isPending || tradeLicenseUpload.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    suspendDraftPersistenceRef.current = true;
    suspendValidationCaptureRef.current = true;
    clearEditableFields();
    clearFormDraft(draftStorageKey);
    form.reset(baseValuesFromBackend ?? portLicenseRequestDefaultValues);

    handleTradeLicenseRemove();
    setFilesForKey(F.PORT_OPERATOR_LICENSE_DOCUMENT_URL, []);
    setFilesForKey(F.OTHERS_DOCUMENT_URL, []);

    navigate(
      isRenewalMode && renewalId
        ? `/${paths.licenses.index}/${paths.licenses.licenseDetailsById.replace(":id", renewalId)}?licenseType=PORT_OPERATOR_LICENSE`
        : `/${paths.licenses.index}`,
    );
  };

  const cancelDialogProps = {
    testId: `${TEST_ID_BASE}-cancel-dialog`,
    title: t(`${LANG_BASE}.cancel-dialog-title`),
    cancelButtonText: t("common.no"),
    confirmButtonText: t("common.yes"),
    handleCloseDialog: () => setIsCancelDialogOpen(false),
    confirmAction: () => {
      setIsCancelDialogOpen(false);
      handleCancel();
    },
    elementContent: (
      <div className="text-left">
        <p>{t(`${LANG_BASE}.cancel-dialog-description`)}</p>
      </div>
    ),
  };

  const isUploading = uploadOthers.isPending || tradeLicenseUpload.isPending;
  const isSubmitting = createRequest.isPending;

  if (isRenewalMode && data && !canAccessRenewal) {
    return null;
  }

  return (
    <div dir={dir} className="min-h-screen bg-white flex flex-col">
      <Subheader
        testId={`${TEST_ID_BASE}-subheader`}
        title={
          isRenewalMode
            ? t(`${LANG_BASE}.port-operator-license-renewal-title`)
            : t(`${LANG_BASE}.port-operator-license-request-title`)
        }
      />

      <div className="px-24 pt-8 pb-8 flex-1 flex flex-col">
        <FormProvider {...form}>
          <form
            onSubmit={(e) => {
              const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLElement | null;

              if (submitter?.dataset.testid?.endsWith("-delete")) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              if (isUploading) {
                e.preventDefault();
                notify({
                  title: t("common.notification.please-wait-for-upload"),
                  type: "warning",
                });
                return;
              }

              form.handleSubmit(onSubmit, handleInvalidSubmit)(e);
            }}
            className="w-full max-w-[980px] mx-auto flex flex-col gap-6"
          >
            <FormSection title={t(`${LANG_BASE}.vendor-license-request-required-information`)}>
              <FormItem<PortLicenseRequestFormData, typeof F.COMPANY_NAME>
                disabled={isFieldDisabled(F.COMPANY_NAME)}
                required
                name={F.COMPANY_NAME}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-company-name`)}
                testId={`${TEST_ID_BASE}-companyName`}
              />
              <div />

              <FormItem<PortLicenseRequestFormData, typeof F.COMPANY_NAME_ARABIC>
                dir="rtl"
                disabled={isFieldDisabled(F.COMPANY_NAME_ARABIC)}
                required
                name={F.COMPANY_NAME_ARABIC}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-company-name-arabic`)}
                testId={`${TEST_ID_BASE}-companyNameArabic`}
                actionInLabel
                textForActionInLabel={t("common.translate")}
                onActionClicked={() =>
                  redirectForTranslateFromEnglishToArabic(form.getValues("companyName"))
                }
              />

              <FormItem<PortLicenseRequestFormData, typeof F.COMPANY_TYPES>
                disabled={isFieldDisabled(F.COMPANY_TYPES)}
                required
                name={F.COMPANY_TYPES}
                type="multiple-select"
                options={companyTypeOptions}
                label={t(`${LANG_BASE}.vendor-license-request-company-type`)}
                testId={`${TEST_ID_BASE}-companyType`}
              />
              <FormItem<PortLicenseRequestFormData, typeof F.NATURE_OF_BUSINESS_IN_POF>
                disabled={isFieldDisabled(F.NATURE_OF_BUSINESS_IN_POF)}
                required
                name={F.NATURE_OF_BUSINESS_IN_POF}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-nature-of-business`)}
                testId={`${TEST_ID_BASE}-natureOfBusinessInPof`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.COMPANY_EMAIL>
                disabled={isFieldDisabled(F.COMPANY_EMAIL)}
                required
                name={F.COMPANY_EMAIL}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-company-email`)}
                testId={`${TEST_ID_BASE}-companyEmail`}
              />

              <div className="md:col-span-full">
                <FormItem<PortLicenseRequestFormData, typeof F.COMPANY_ADDRESS>
                  disabled={isFieldDisabled(F.COMPANY_ADDRESS)}
                  required
                  name={F.COMPANY_ADDRESS}
                  type="text"
                  maxChars={250}
                  label={t(`${LANG_BASE}.vendor-license-request-company-address`)}
                  testId={`${TEST_ID_BASE}-companyAddress`}
                />
              </div>

              <FormItem<PortLicenseRequestFormData, typeof F.APPLICANT_NAME>
                disabled={isFieldDisabled(F.APPLICANT_NAME)}
                required
                name={F.APPLICANT_NAME}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-name-of-applicant`)}
                testId={`${TEST_ID_BASE}-applicantName`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.APPLICANT_POSITION>
                disabled={isFieldDisabled(F.APPLICANT_POSITION)}
                required
                name={F.APPLICANT_POSITION}
                type="text"
                maxChars={100}
                label={t(`${LANG_BASE}.vendor-license-request-applicant-position`)}
                testId={`${TEST_ID_BASE}-applicantPosition`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.APPLICANT_EMAIL>
                disabled={isRenewalMode || isFieldDisabled(F.APPLICANT_EMAIL)}
                required
                name={F.APPLICANT_EMAIL}
                type="text"
                maxChars={150}
                label={t(`${LANG_BASE}.vendor-license-request-email-address`)}
                testId={`${TEST_ID_BASE}-applicantEmail`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.TELEPHONE_NUMBER>
                disabled={isFieldDisabled(F.TELEPHONE_NUMBER)}
                required
                name={F.TELEPHONE_NUMBER}
                type="text"
                maxChars={30}
                label={t(`${LANG_BASE}.vendor-license-request-telephone-no`)}
                testId={`${TEST_ID_BASE}-telephoneNumber`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.MOBILE_NUMBER>
                disabled={isFieldDisabled(F.MOBILE_NUMBER)}
                required
                name={F.MOBILE_NUMBER}
                type="text"
                maxChars={30}
                label={t(`${LANG_BASE}.vendor-license-request-mobile-number`)}
                testId={`${TEST_ID_BASE}-mobileNumber`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.FAX_NUMBER>
                disabled={isFieldDisabled(F.FAX_NUMBER)}
                required
                name={F.FAX_NUMBER}
                type="text"
                label={t(`${LANG_BASE}.vendor-license-request-fax-number`)}
                testId={`${TEST_ID_BASE}-faxNumber`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.TRADING_LICENSE_NUMBER>
                disabled={isFieldDisabled(F.TRADING_LICENSE_NUMBER)}
                required={isTradeLicenseRequired}
                name={F.TRADING_LICENSE_NUMBER}
                type="number"
                maxDigits={10}
                canBeginWithZero
                disableNativeNumberPattern={isRenewalMode}
                label={t(`${LANG_BASE}.vendor-license-request-trade-license-number`)}
                testId={`${TEST_ID_BASE}-tradingLicenseNumber`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.TRADING_LICENSE_VALIDITY_DATE>
                disabled={isFieldDisabled(F.TRADING_LICENSE_VALIDITY_DATE)}
                required={isTradeLicenseRequired}
                name={F.TRADING_LICENSE_VALIDITY_DATE}
                type="date-picker"
                label={t(`${LANG_BASE}.vendor-license-request-trade-license-validity-date`)}
                timePicker={false}
                minDate={new Date()}
                testId={`${TEST_ID_BASE}-tradingLicenseValidityDate`}
              />

              <FormItem<PortLicenseRequestFormData, typeof F.TRADE_LICENSE_ISSUING_AUTHORITY>
                disabled={isFieldDisabled(F.TRADE_LICENSE_ISSUING_AUTHORITY)}
                required={isTradeLicenseRequired}
                name={F.TRADE_LICENSE_ISSUING_AUTHORITY}
                type="autocomplete"
                options={tradeLicenseIssuingAuthorityOptions}
                label={t(`${LANG_BASE}.vendor-license-request-trade-license-issuing-authority`)}
                testId={`${TEST_ID_BASE}-tradeLicenseIssuingAuthority`}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-[inherit]">
                <FormItem<PortLicenseRequestFormData, typeof F.NUMBER_OF_EMPLOYEES>
                  disabled={isFieldDisabled(F.NUMBER_OF_EMPLOYEES)}
                  required
                  name={F.NUMBER_OF_EMPLOYEES}
                  type="number"
                  maxDigits={3}
                  canBeginWithZero
                  disableNativeNumberPattern={isRenewalMode}
                  label={t(`${LANG_BASE}.vendor-license-request-no-of-employees`)}
                  testId={`${TEST_ID_BASE}-numberOfEmployees`}
                />

                <FormItem<PortLicenseRequestFormData, typeof F.NUMBER_OF_VEHICLES>
                  disabled={isFieldDisabled(F.NUMBER_OF_VEHICLES)}
                  required
                  name={F.NUMBER_OF_VEHICLES}
                  type="number"
                  maxDigits={3}
                  canBeginWithZero
                  disableNativeNumberPattern={isRenewalMode}
                  label={t(`${LANG_BASE}.vendor-license-request-no-of-vehicles`)}
                  testId={`${TEST_ID_BASE}-numberOfVehicles`}
                />
              </div>

              <FormItem<PortLicenseRequestFormData, typeof F.PURPOSE_OF_REQUEST>
                disabled={isFieldDisabled(F.PURPOSE_OF_REQUEST)}
                required
                name={F.PURPOSE_OF_REQUEST}
                type="text"
                maxChars={200}
                label={t(`${LANG_BASE}.vendor-license-request-purpose-of-request`)}
                testId={`${TEST_ID_BASE}-purposeOfRequest`}
              />
              <div className="col-span-full">
                <FormItem<PortLicenseRequestFormData, typeof F.FUJAIRAH_REGISTERED_COMPANY>
                  required
                  name={F.FUJAIRAH_REGISTERED_COMPANY}
                  type="radio"
                  horizontalRadioButtons
                  label={t("licensing.fujairah-registered-company")}
                  options={[
                    { value: true, label: t("common.yes") },
                    { value: false, label: t("common.no") },
                  ]}
                  testId={`${TEST_ID_BASE}-fujairahRegisteredCompany`}
                />
              </div>
            </FormSection>
            <FormSection title={t(`${LANG_BASE}.vendor-license-request-trade-license-doc-title`)}>
              <div className="md:col-span-full">
                {tradeUrl ? (
                  <LicenseCard
                    testId={`${TEST_ID_BASE}-document-trade-license`}
                    title={t("licensing.tradeLicense")}
                    fileName={
                      readonlyTradeLicense?.name ||
                      `trade_license.${getFileExtensionFromUrl(tradeUrl)}`
                    }
                    fileUrl={tradeUrl}
                    onPreview={() => handlePreview("bff-licensing", tradeUrl)}
                    onDownload={() =>
                      handleDownload("bff-licensing", tradeUrl, DocumentTypes.TRADE_LICENSE)
                    }
                  />
                ) : (
                  <FormItem<PortLicenseRequestFormData, typeof F.TRADE_LICENSE_DOCUMENT_URL>
                    required={isTradeLicenseRequired}
                    name={F.TRADE_LICENSE_DOCUMENT_URL}
                    type="file"
                    maxFiles={1}
                    testId={`${TEST_ID_BASE}-tradeLicenseUpload`}
                    allowedFileExtensions={allowedExtensions}
                    uploadConditions={documentConditions}
                    uploadDescription={t("upload.additional-documents-upload", {
                      extensions: (documentConditions.format ?? []).join(", "),
                      size: documentConditions.maxSizeLabel,
                    })}
                    files={tradeLicenseUpload.uploadEntries}
                    setFiles={(files) =>
                      tradeLicenseUpload.setUploadEntries((files ?? []) as UploadEntry[])
                    }
                    onUpload={(_, files) => handleTradeLicenseUpload((files ?? []) as File[])}
                    onRemoveFile={handleTradeLicenseRemove}
                    notSuccess={tradeLicenseUpload.notSuccess}
                    disabled={
                      tradeLicenseUpload.isPending ||
                      (isRenewalMode && tradeLicenseUpload.uploadEntries.length > 0)
                    }
                  />
                )}
              </div>

              {!tradeUrl && tradeLicenseUpload.uploadEntries.length > 0 && (
                <div className="md:col-span-full">
                  <SingleFile
                    testId={`${TEST_ID_BASE}-tradeLicense-file`}
                    file={tradeLicenseUpload.uploadEntries[0]}
                    fieldName={F.TRADE_LICENSE_DOCUMENT_URL}
                    setFiles={() => tradeLicenseUpload.setUploadEntries([])}
                    removeFn={handleTradeLicenseRemove}
                    isLoading={tradeLicenseUpload.uploadEntries[0] instanceof File}
                  />
                </div>
              )}
            </FormSection>

            <FormSection title={t("licensing.other-documents-title")} isFullWidth>
              <div className="col-span-full">
                <FormItem<PortLicenseRequestFormData, "othersDocumentUrl">
                  name={othersFieldName}
                  type="file"
                  testId={`${TEST_ID_BASE}-others-upload`}
                  uploadConditions={documentConditions}
                  maxFiles={10}
                  allowedFileExtensions={allowedExtensions}
                  uploadDescription={t("upload.additional-documents-upload", {
                    extensions: (documentConditions.format ?? []).join(", "),
                    size: documentConditions.maxSizeLabel,
                  })}
                  onUpload={handleUploadOthers}
                  files={otherFiles}
                  setFiles={(files) => setFilesForKey(othersFieldName, files)}
                  disabled={uploadOthers.isPending}
                />
              </div>
              <div className="flex gap-4 w-full flex-wrap">
                {otherFiles.map((file, idx) => (
                  <SingleFile
                    key={`${othersFieldName}-${idx}`}
                    testId={`${TEST_ID_BASE}-others-${idx}`}
                    file={file}
                    fieldName={othersFieldName}
                    currentFileIndex={idx}
                    isLoading={otherFiles[idx] instanceof File}
                    setFiles={() => handleRemoveFile(idx)}
                  />
                ))}
              </div>
            </FormSection>

            <div className="flex items-center justify-between pt-2">
              <Button
                testId={`${TEST_ID_BASE}-cancel`}
                variant="outlined"
                type="button"
                className="whitespace-nowrap"
                sx={{ textTransform: "none" }}
                disabled={isSubmitting}
                onClick={() => setIsCancelDialogOpen(true)}
                label={t("common.cancel")}
              />

              <Button
                testId={`${TEST_ID_BASE}-submit`}
                variant="contained"
                type="submit"
                className="whitespace-nowrap"
                sx={{ textTransform: "none" }}
                startIcon={<ContentPasteGo />}
                disabled={isSubmitting}
                label={t(`${LANG_BASE}.port-operator-license-request-submit`)}
              />
            </div>
          </form>
        </FormProvider>
      </div>
      <GenericDialog open={isCancelDialogOpen} {...cancelDialogProps} />
    </div>
  );
};

export default PortLicenseRequestPage;
